import type { NetworkModelComponent } from "@/endlaFunctions/customTypings/infrastructure";
import type { DirectedGraph } from "graphology";

export enum TraversalDirection {
  // Returns all nodes and edges that are upstream of the source node
  UPSTREAM = "UPSTREAM",
  // Returns all nodes and edges that are downstream of the source node
  DOWNSTREAM = "DOWNSTREAM",
  // Returns all nodes and edges that are connected to the source node
  CONNECTED = "CONNECTED",
}
/**
 * Finds connected component in undirected graph
 * Surprisingly graphology doesn't have the exact function built in
 */
export function getConnectedComponent(
  graph: DirectedGraph,
  sourceNode: PrimaryKey,
  removeEdgesSet: Set<PrimaryKey>,
  traversalDirection: TraversalDirection = TraversalDirection.CONNECTED,
  options: {
    isHydraulic?: boolean;
    stopAtComponents?: NetworkModelComponent[];
  } = {}
): { edges: Set<PrimaryKey>; nodes: Set<PrimaryKey> } {
  const { isHydraulic = false, stopAtComponents = ["sink", "tank"] } = options;

  const stack = [String(sourceNode)];
  const connectedComponent = new Set<PrimaryKey>();
  const seenNodes = new Set<PrimaryKey>([sourceNode]);

  if (!graph.hasNode(sourceNode)) {
    return { edges: connectedComponent, nodes: seenNodes };
  }

  while (stack.length > 0) {
    const currentNode = stack.pop();
    seenNodes.add(Number(currentNode));

    // For hydraulic mode, check if we should stop at this node
    if (isHydraulic && stopAtComponents.length > 0) {
      try {
        const comp = graph.getNodeAttribute(currentNode, "network_model_component");

        if (stopAtComponents.includes(comp)) {
          // We include this node but don't traverse through it
          continue;
        }
      } catch (_e) {
        // If the node doesn't have the attribute, we can continue
        // This is a defensive measure against potential errors
      }
    }

    let unfilteredEdges: string[] = [];
    switch (traversalDirection) {
      case TraversalDirection.UPSTREAM:
        unfilteredEdges = graph.inboundEdges(currentNode);
        break;
      case TraversalDirection.DOWNSTREAM:
        unfilteredEdges = graph.outboundEdges(currentNode);
        break;
      case TraversalDirection.CONNECTED:
        unfilteredEdges = graph.edges(currentNode);
        break;
    }

    const edges = unfilteredEdges.filter(
      (edge: string) => !removeEdgesSet.has(Number(edge)) && !connectedComponent.has(Number(edge))
    );

    for (const edge of edges) {
      connectedComponent.add(Number(edge));
      const targetNode = graph.opposite(currentNode, edge);

      // For hydraulic mode, check if the next node is a stopping component
      if (isHydraulic && stopAtComponents.length > 0) {
        try {
          const nextComp = graph.getNodeAttribute(targetNode, "network_model_component");

          if (stopAtComponents.includes(nextComp)) {
            // We add the edge but don't continue past this node
            continue;
          }
        } catch (_e) {
          // If the node doesn't have the attribute, proceed
        }
      }

      // Only add to stack if we haven't seen this node yet
      if (!seenNodes.has(Number(targetNode))) {
        stack.push(targetNode);
      }
    }
  }

  return { edges: connectedComponent, nodes: seenNodes };
}
